/**
 * Google sign-in. On the website (`startWebGoogleLogin`) the page goes to Google, which comes back to
 * /auth/callback (pages/AuthCallback.jsx: the code is traded for a session, then the page the player
 * started from, kept in localStorage.post_login_redirect). In the Android / iOS apps it works as in the
 * owner's other app (BudSmart), below. `startGoogleLogin` picks the one that fits.
 *
 * The app opens the backend's GET /api/auth/google/app in an in-app browser tab (@capacitor/browser):
 * straight on to Google's account choice. Google comes back to the backend, which sends the browser
 * straight into the app — com.ramitounsia.app://auth/callback?session_id=… — with no page of the
 * website and no button: Android brings the app back and closes the tab, iOS closes it here. The app
 * trades the one-time id for its session (backend/auth.py).
 *
 * The app also sends the hash of a secret it keeps: an app that caught the return link cannot trade
 * the id.
 */
import { Capacitor } from "@capacitor/core";
import { api, API, saveSessionToken } from "./api";

// The app's id (capacitor.config.json), declared as its link in AndroidManifest.xml and Info.plist,
// and the backend's APP_RETURN.
export const APP_RETURN = "com.ramitounsia.app://auth/callback";
const PENDING_KEY = "rami_google_app_login";
const PENDING_MAX_AGE = 15 * 60 * 1000;

export const isNativeApp = () => Capacitor.isNativePlatform();

/** The website: off to Google's account choice; it comes back to /auth/callback. */
export async function startWebGoogleLogin(redirectTo) {
  const { data } = await api.get("/auth/google");
  try {
    if (redirectTo) localStorage.setItem("post_login_redirect", redirectTo);
    else localStorage.removeItem("post_login_redirect");
  } catch {}
  window.location.assign(data.url);
}

/** Google sign-in, the website's or the app's. `redirectTo`: the page to open once signed in. */
export const startGoogleLogin = (redirectTo) =>
  isNativeApp() ? startAppGoogleLogin(redirectTo) : startWebGoogleLogin(redirectTo);

const base64url = (bytes) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

/** Opens Google in the app's browser tab. `redirectTo`: the page to open once signed in. */
export async function startAppGoogleLogin(redirectTo) {
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
  const challenge = base64url(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)));
  localStorage.setItem(PENDING_KEY, JSON.stringify({ verifier, redirectTo: redirectTo || null, at: Date.now() }));
  const { Browser } = await import("@capacitor/browser");
  await Browser.open({ url: `${API}/auth/google/app?challenge=${challenge}` });
}

let lastUrl = null;

/**
 * The link that brought the app back (App's appUrlOpen, or its launch URL after a cold start).
 * Resolves to null when it is not a sign-in return (or one already handled), else to
 * { user, redirectTo } once signed in, or { error } ("access_denied" when the player cancelled).
 */
export async function finishAppGoogleLogin(url) {
  if (!url || !url.startsWith(APP_RETURN) || url === lastUrl) return null;
  lastUrl = url;
  let pending = null;
  try { pending = JSON.parse(localStorage.getItem(PENDING_KEY)); } catch {}
  localStorage.removeItem(PENDING_KEY);
  // The tab closes by itself on Android (the app's activity is singleTask); iOS needs telling.
  import("@capacitor/browser").then(({ Browser }) => Browser.close()).catch(() => {});

  const params = new URL(url).searchParams;
  const sessionId = params.get("session_id");
  if (!sessionId) return { error: params.get("error") || "no_session" };
  if (!pending || Date.now() - pending.at > PENDING_MAX_AGE) return { error: "stale" };
  try {
    const { data } = await api.post("/auth/google/app/session", { session_id: sessionId, code_verifier: pending.verifier });
    saveSessionToken(data.session_token);
    return { user: data.user, redirectTo: pending.redirectTo };
  } catch (e) {
    console.warn("[google] sign-in", e);
    return { error: "exchange" };
  }
}
